import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { DatePicker } from '@/components/ui/DatePicker';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/store/toastStore';
import { listUsers } from '@/services/admin.service';
import {
  cancelKbAssignment,
  createKbAssignment,
  kbQueryKeys,
  listKbAssignments,
  listKbChapters,
  listKnowledgeBaseDocuments,
  type KbAssignment,
} from '@/services/knowledge-base.service';

function statusBadge(status: string) {
  if (status === 'acknowledged') return <Badge variant="success">Acknowledged</Badge>;
  if (status === 'overdue') return <Badge variant="error">Overdue</Badge>;
  if (status === 'canceled') return <Badge variant="neutral">Canceled</Badge>;
  return <Badge variant="warning">Pending</Badge>;
}

export function AssignmentsPage() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState<number | null>(null);

  const [targetType, setTargetType] = useState<'document' | 'chapter'>('document');
  const [targetId, setTargetId] = useState<number>(0);
  const [docSearch, setDocSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [dueAt, setDueAt] = useState<string>('');

  const assignmentsQuery = useQuery({
    queryKey: kbQueryKeys.assignments(),
    queryFn: () => listKbAssignments(),
  });

  const usersQuery = useQuery({
    queryKey: ['kb', 'users-pick'],
    queryFn: listUsers,
    enabled: showCreate,
  });

  const docsQuery = useQuery({
    queryKey: kbQueryKeys.documents({ q: docSearch.trim() || undefined, perPage: 25 }),
    queryFn: () => listKnowledgeBaseDocuments({ q: docSearch.trim() || undefined, perPage: 25 }),
    enabled: showCreate && targetType === 'document',
  });

  const chaptersListQuery = useQuery({
    queryKey: kbQueryKeys.chapters(),
    queryFn: () => listKbChapters(),
    enabled: showCreate && targetType === 'chapter',
  });

  const usersById = useMemo(() => {
    const map = new Map<number, string>();
    (usersQuery.data ?? []).forEach((u) => {
      map.set(u.userId, `${u.firstName} ${u.lastName}`.trim() || u.userName);
    });
    return map;
  }, [usersQuery.data]);

  function userName(userId: number) {
    return usersById.get(userId) ?? `User #${userId}`;
  }

  const createMutation = useMutation({
    mutationFn: () =>
      createKbAssignment({
        targetType,
        targetId,
        userIds: selectedUserIds,
        dueAt: dueAt || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.all });
      toast.success(`Assigned to ${selectedUserIds.length} user${selectedUserIds.length === 1 ? '' : 's'}`);
      setShowCreate(false);
      setSelectedUserIds([]);
      setTargetId(0);
      setDueAt('');
    },
    onError: () => toast.error('Failed to create assignment'),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: number) => cancelKbAssignment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.assignments() });
      toast.success('Assignment canceled');
      setConfirmCancel(null);
    },
    onError: () => toast.error('Failed to cancel'),
  });

  function toggleUser(userId: number) {
    setSelectedUserIds((curr) =>
      curr.includes(userId) ? curr.filter((id) => id !== userId) : [...curr, userId],
    );
  }

  function handleSubmit() {
    if (!targetId) {
      toast.error('Pick a document or chapter to assign');
      return;
    }
    if (selectedUserIds.length === 0) {
      toast.error('Pick at least one user');
      return;
    }
    createMutation.mutate();
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowCreate(true)}>
          New Assignment
        </Button>
      </div>

      {assignmentsQuery.isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Loading assignments...
        </div>
      ) : !assignmentsQuery.data?.length ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          No assignments yet. Click "New Assignment" to assign a document or chapter to staff.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">User</th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Target</th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Due</th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {assignmentsQuery.data.map((a: KbAssignment) => (
                <tr key={a.kbAssignmentId} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-sm text-slate-900">{userName(a.assignedUserId)}</td>
                  <td className="px-4 py-2 text-sm text-slate-700">
                    <span className="text-xs text-slate-500">{a.targetType}:</span> {a.targetTitle || `#${a.targetId}`}
                  </td>
                  <td className="px-4 py-2 text-sm text-slate-700">
                    {a.dueAt ? new Date(a.dueAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-2">{statusBadge(a.status)}</td>
                  <td className="px-4 py-2 text-right">
                    {a.status === 'pending' || a.status === 'overdue' ? (
                      <Button
                        size="xs"
                        variant="ghost"
                        aria-label="Cancel assignment"
                        onClick={() => setConfirmCancel(a.kbAssignmentId)}
                      >
                        <XCircle className="h-4 w-4 text-red-500" />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Assign Training"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={createMutation.isPending}>
              Assign
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="What are you assigning?">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setTargetType('document');
                  setTargetId(0);
                }}
                className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                  targetType === 'document'
                    ? 'border-blue-500 bg-blue-50 font-medium text-blue-700'
                    : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                Document
              </button>
              <button
                type="button"
                onClick={() => {
                  setTargetType('chapter');
                  setTargetId(0);
                }}
                className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                  targetType === 'chapter'
                    ? 'border-blue-500 bg-blue-50 font-medium text-blue-700'
                    : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                Chapter
              </button>
            </div>
          </FormField>

          {targetType === 'document' ? (
            <>
              <Input
                placeholder="Search documents..."
                value={docSearch}
                onChange={(e) => setDocSearch(e.target.value)}
              />
              <div className="max-h-[180px] overflow-y-auto rounded-md border border-slate-200">
                {docsQuery.data?.data.map((d) => (
                  <button
                    key={d.kbDocumentId}
                    type="button"
                    onClick={() => setTargetId(d.kbDocumentId)}
                    className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                      targetId === d.kbDocumentId ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-50'
                    }`}
                  >
                    <span className="truncate">{d.title}</span>
                    <span className="text-xs text-slate-500">{d.category || ''}</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="max-h-[180px] overflow-y-auto rounded-md border border-slate-200">
              {chaptersListQuery.data?.map((c) => (
                <button
                  key={c.kbChapterId}
                  type="button"
                  onClick={() => setTargetId(c.kbChapterId)}
                  className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                    targetId === c.kbChapterId ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-50'
                  }`}
                >
                  <span className="truncate">{c.title}</span>
                  <span className="text-xs text-slate-500">{c.itemCount} docs</span>
                </button>
              ))}
            </div>
          )}

          <FormField label="Assign to">
            <div className="max-h-[200px] overflow-y-auto rounded-md border border-slate-200">
              {(usersQuery.data ?? []).filter((u) => u.active).map((u) => (
                <label
                  key={u.userId}
                  className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={selectedUserIds.includes(u.userId)}
                    onChange={() => toggleUser(u.userId)}
                  />
                  <span>{`${u.firstName} ${u.lastName}`.trim() || u.userName}</span>
                  <span className="ml-auto text-xs text-slate-400">{u.email}</span>
                </label>
              ))}
            </div>
          </FormField>

          {/* SCRUM-1466 / SCRUM-1467. This was a raw `<input type="date">` carrying only
              `form-input-base`, which cost it three things the shared component supplies:
              the calendar icon (index.css strips the UA's own affordance below `md` with
              `max-md:appearance-none`), a working placeholder (a native date control
              ignores the attribute — DatePicker paints an overlay instead), and the label
              association, which a bare input inside FormField never had.

              `clearable` is the SCRUM-1466 half. The field is optional and the mutation
              already submits `dueAt || null`, but once a value was picked there was no way
              back to '' from inside the app — the only reset on offer was the platform
              picker's own, which on Android writes today's date. */}
          <FormField label="Due date (optional)" htmlFor="kb-due-at">
            <DatePicker
              id="kb-due-at"
              value={dueAt}
              onChange={setDueAt}
              clearable
              placeholder="No due date"
            />
          </FormField>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirmCancel !== null}
        onClose={() => setConfirmCancel(null)}
        onConfirm={() => {
          if (confirmCancel !== null) cancelMutation.mutate(confirmCancel);
        }}
        title="Cancel assignment?"
        message="The user will no longer see this in their training queue."
        confirmLabel="Cancel Assignment"
        variant="warning"
      />
    </div>
  );
}
